import { Button, Modal, Space, Table, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import Layout from './Layout';
import { useDispatch, useSelector } from 'react-redux';
import {
    investorIncomeTrustDivestmentRequestCancelAction,
    investorIncomeTrustDivestmentRequestProcessAction,
    investorIncomeTrustsDivestmentRequestsPendingListAction,
} from '~Investments/actions';
import {
    investorIncomeTrustDivestmentRequestsProcessInProgressSelector,
    investorIncomeTrustsDivestmentRequestsPendingSelector,
} from '~Investments/selectors';
import IInvestorIncomeTrustDivestmentRequest from '~Api/IncomeTrust/IInvestorIncomeTrustDivestmentRequest';
import { IDictionary } from '~utilities/IDictionary';
import { Dispatch } from 'redux';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import IInvestorAccountIncomeTrust from '~Api/IncomeTrust/IInvestorAccountIncomeTrust';
import classTypeLabels from '~Api/IncomeTrust/classTypeLabels';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import { currencyFormatter } from '~utilities/formatters';
import { Helmet } from 'react-helmet';
import InvestorIncomeTrustDivestmentRequestStatusEnum from '~Api/IncomeTrust/InvestorIncomeTrustDivestmentRequestStatusEnum';
import './pending-divestments.less';

interface IPendingDivestmentRow {
    divestmentRequest: IInvestorIncomeTrustDivestmentRequest;
    divestmentRequestUuid: string;
    processableError?: string;
}

export default function PendingDivestments(): ReactElement {
    const pendingDivestments: IDictionary<IInvestorIncomeTrustDivestmentRequest> = useSelector(investorIncomeTrustsDivestmentRequestsPendingSelector);
    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const processInProgress: IDictionary<boolean> = useSelector(investorIncomeTrustDivestmentRequestsProcessInProgressSelector);
    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!pendingDivestments) {
            dispatch(investorIncomeTrustsDivestmentRequestsPendingListAction());
        }
    }, [
        dispatch,
        pendingDivestments,
    ]);

    const onClickProcessDivestmentRequest: (pendingDivestmentUuid: string) => void = useCallback((pendingDivestmentUuid: string): void => {
        Modal.confirm({
            cancelText: 'Close',
            content: 'Are you sure you want to process this divestment request?',
            okText: 'Confirm',
            onOk: (): void => {
                dispatch(investorIncomeTrustDivestmentRequestProcessAction(pendingDivestmentUuid));
            },
            title: 'Process Divestment Request',
        });
    }, [
        dispatch,
    ]);

    const onClickCancelDivestmentRequest: (pendingDivestmentUuid: string) => void = useCallback((pendingDivestmentUuid: string): void => {
        Modal.confirm({
            cancelText: 'Close',
            content: 'Are you sure you want to cancel this divestment request?',
            okButtonProps: { danger: true },
            okText: 'Confirm',
            onOk: (): void => {
                dispatch(investorIncomeTrustDivestmentRequestCancelAction(pendingDivestmentUuid));
            },
            title: 'Cancel Divestment Request',
        });
    }, [
        dispatch,
    ]);

    const columns: ColumnsType<IPendingDivestmentRow> = [
        {
            defaultSortOrder: 'ascend',
            render: (pendingDivestmentRow: IPendingDivestmentRow): ReactNode => {
                const processableErrorBlock: ReactNode = pendingDivestmentRow.processableError && (
                    <Tooltip title={pendingDivestmentRow.processableError}><WarningOutlined/></Tooltip>
                );

                return (
                    <Space>
                        <>{dayjs(pendingDivestmentRow.divestmentRequest.divestmentDate).format('DD/MM/YYYY')}</>
                        {processableErrorBlock}
                    </Space>
                );
            },
            sorter: (a: IPendingDivestmentRow, b: IPendingDivestmentRow): number => {
                return dayjs(a.divestmentRequest.divestmentDate) > dayjs(b.divestmentRequest.divestmentDate) ? 1 : -1;
            },
            title: 'Divestment Date',
            width: 160,
        },
        {
            dataIndex: [ 'divestmentRequest', 'amount' ],
            render: (amount: number): ReactNode => currencyFormatter.format(amount),
            title: 'Amount',
            width: 220,
        },
        {
            dataIndex: [ 'divestmentRequest', 'investorAccountIncomeTrust' ],
            render: (investorAccountIncomeTrust: IInvestorAccountIncomeTrust): ReactNode => {
                return (
                    <Link to={`/investors/${investorAccountIncomeTrust.investorUuid}/accounts/${investorAccountIncomeTrust.investorAccountUuid}`}>
                        {investorAccountIncomeTrust.investorName}
                    </Link>
                );
            },
            title: 'Investor',
        },
        {
            dataIndex: [ 'divestmentRequest', 'investorAccountIncomeTrust' ],
            render: (investorAccountIncomeTrust: IInvestorAccountIncomeTrust): ReactNode => classTypeLabels[investorAccountIncomeTrust.classType],
            title: 'Class',
            width: 140,
        },
        {
            dataIndex: [ 'divestmentRequest', 'investorAccountIncomeTrust' ],
            render: (investorAccountIncomeTrust: IInvestorAccountIncomeTrust): ReactNode => {
                return currencyFormatter.format(investorAccountIncomeTrust.principalBalance);
            },
            title: 'Balance',
            width: 220,
        },
        {
            dataIndex: [ 'divestmentRequest', 'requestedTime' ],
            render: (requestedTime: string): ReactNode => dayjs(requestedTime).format('DD/MM/YYYY'),
            title: 'Requested Date',
            width: 160,
        },
        ...(currentUser.permissions.includes(PermissionsEnum.Accounts) ? [
            {
                render: (pendingDivestmentRow: IPendingDivestmentRow): ReactNode => {
                    if (InvestorIncomeTrustDivestmentRequestStatusEnum.Pending !== pendingDivestmentRow.divestmentRequest.status) {
                        return null;
                    }

                    const onClickProcess: () => void = (): void => onClickProcessDivestmentRequest(pendingDivestmentRow.divestmentRequest.uuid);
                    const onClickCancel: () => void = (): void => onClickCancelDivestmentRequest(pendingDivestmentRow.divestmentRequest.uuid);
                    const isInProgress: boolean = !!processInProgress[pendingDivestmentRow.divestmentRequest.uuid];

                    const processButton: ReactNode = !pendingDivestmentRow.processableError && (
                        <Button disabled={isInProgress} onClick={onClickProcess} type='primary'>Process</Button>
                    );

                    return (
                        <Space>
                            {processButton}
                            <Button danger={true} disabled={isInProgress} onClick={onClickCancel}>Cancel</Button>
                        </Space>
                    );
                },
                title: 'Actions',
                width: 140,
            },
        ] : []),
    ];

    const rows: IPendingDivestmentRow[] = _.map(
        pendingDivestments,
        (divestmentRequest: IInvestorIncomeTrustDivestmentRequest): IPendingDivestmentRow => {
            const divestmentDateDayJs: Dayjs = dayjs(divestmentRequest.divestmentDate);
            let processableError: string;
            if (divestmentDateDayJs.isAfter(dayjs())) {
                processableError = `Cannot process until ${divestmentDateDayJs.format('DD/MM/YYYY')}.`;
            } else if (divestmentDateDayJs.isBefore(dayjs().subtract(1, 'week'))) {
                processableError = 'Cannot process divestment scheduled for a date more than a week ago.';
            }

            return {
                divestmentRequest,
                divestmentRequestUuid: divestmentRequest.uuid,
                processableError,
            };
        },
    );

    return (
        <Layout section='pending-divestments'>
            <Helmet>
                <title>Pending Divestments</title>
            </Helmet>
            <Typography.Title level={3}>Pending Divestments</Typography.Title>
            <Table
                columns={columns}
                dataSource={rows}
                loading={!pendingDivestments}
                pagination={false}
                rowKey='divestmentRequestUuid'
                size='middle'
            />
        </Layout>
    );
}
